import React, { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import HomePage from "./pages/HomePage";
import SignPage from "./pages/sign/SignPage";
import NotFoundPage from "./pages/NotFoundPage";
import {
  CompanyLogin,
  CompanyDashboard,
  CompanySettings,
  CompanyContracts,
  CompanyClients,
  CompanyTemplates,
  CompanyWebhooks,
  CompanyUsage,
} from "./pages/company";
import { ApiDocs } from "./pages/docs";
import { DashboardLayout } from "./layouts";
import { isAuthenticated } from "./services/companyAdminService";
import { AppProvider } from "./context/AppContext";
import { Provider, useDispatch } from "react-redux";
import { store } from "./store";
import { setCredentials } from "./store/authSlice";

// Protected route wrapper for company admin
const ProtectedCompanyRoute = ({ children }) => {
  if (!isAuthenticated()) {
    return <Navigate to="/company/login" replace />;
  }
  return children;
};

// Handle Google OAuth callback
const GoogleAuthCallback = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const token = searchParams.get("token");
    if (token) {
      localStorage.setItem("companyAdminToken", token);
      dispatch(setCredentials({ token }));
      navigate("/company/dashboard");
    } else {
      navigate("/company/login?error=auth_failed");
    }
  }, [navigate, searchParams, dispatch]);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        background: "#0a0a0a",
        color: "#fafafa",
      }}
    >
      מעביר...
    </div>
  );
};

// Welcome page for new Google signups
const WelcomePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const token = searchParams.get("token");
    if (token) {
      localStorage.setItem("companyAdminToken", token);
    }
  }, [searchParams]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        background: "#0a0a0a",
        color: "#fafafa",
        textAlign: "center",
        padding: "20px",
      }}
    >
      <h1 style={{ fontSize: "48px", marginBottom: "16px" }}>
        🎉 ברוכים הבאים!
      </h1>
      <p style={{ fontSize: "20px", color: "#a1a1a1", marginBottom: "32px" }}>
        החשבון שלך נוצר בהצלחה.
      </p>
      <button
        onClick={() => navigate("/company/dashboard")}
        style={{
          background: "#fafafa",
          color: "#0a0a0a",
          border: "none",
          padding: "12px 24px",
          borderRadius: "6px",
          fontSize: "14px",
          cursor: "pointer",
        }}
      >
        עבור לדשבורד
      </button>
    </div>
  );
};

function App() {
  return (
    <Provider store={store}>
      <AppProvider>
        <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/sign/:token" element={<SignPage />} />

          {/* API Documentation */}
          <Route path="/docs" element={<ApiDocs />} />
          <Route path="/docs/api" element={<ApiDocs />} />

          {/* Company Admin Routes */}
          <Route path="/company/login" element={<CompanyLogin />} />
          <Route
            path="/company/auth/callback"
            element={<GoogleAuthCallback />}
          />
          <Route path="/company/welcome" element={<WelcomePage />} />

          {/* Protected Company Routes with Dashboard Layout */}
          <Route
            path="/company"
            element={
              <ProtectedCompanyRoute>
                <DashboardLayout />
              </ProtectedCompanyRoute>
            }
          >
            <Route
              index
              element={<Navigate to="/company/dashboard" replace />}
            />
            <Route path="dashboard" element={<CompanyDashboard />} />
            <Route path="settings" element={<CompanySettings />} />
            <Route path="contracts" element={<CompanyContracts />} />
            <Route path="clients" element={<CompanyClients />} />
            <Route path="templates" element={<CompanyTemplates />} />
            <Route path="webhooks" element={<CompanyWebhooks />} />
            <Route path="usage" element={<CompanyUsage />} />
          </Route>

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </BrowserRouter>
      </AppProvider>
    </Provider>
  );
}

export default App;
